import React, { useEffect, useState, useCallback } from "react";
import { Skeleton } from "antd";
import { useParams, useSearchParams } from "react-router-dom";
import { IAuditLogResponse, IAuditLogResult } from "../../interfaces/audit-log";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { AuditLogServices } from "../../services/audit-log";
import { ISortInfo } from "../../interfaces/common";
import { formatDateDDMMYYYY, formatDateDDMMYYYY_HHMMSS, formatNumberFixed } from "../../utils/helper";
import BackButton from "../../components/back-button";
import { AuditItem } from "../../components/audit-trail/audit-trail";
import InfiniteAuditTrail from "../../components/audit-trail/infinite-audit-trail";

// MARK: will change later
const fieldLabels: Record<string, string> = {
  supplier_id: "Supplier Id",
  supplier_code: "Supplier Code",
  supplier_name: "Supplier Name",
  booking_ref: "Booking Ref",
  pnr_ref: "PNR Ref",
  currency: "Currency",
  amount: "Amount",
  due_date: "Due Date",
  transaction_date: "Transaction Date",
  is_active: "Action",
  status: "Status",
  generated_by: "Generated By",
  stage_type: "Stage Type",
  stage_order: "Stage Order",
  gbp_cost: "GBP Cost",
  invoice_number: "Invoice Number",
  booking_no: "Booking Reference",
  lead_pax: "Lead Passenger",
  reason: "Rejected Reason",
};

const SIRAudiTrailPage: React.FC = () => {
  const [logs, setLogs] = useState<IAuditLogResult[]>([]);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [sortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const supplierCode = searchParams.get("code");
  const supplierName = searchParams.get("name");

  const { id } = useParams<{ id: string }>();

  const fetchData = async () => {
    if (!id) {
      setLogs([]);
      setInitialLoading(false);
      return;
    }
    if (!hasMore && pagination.current !== 1) return;

    try {
      if (pagination.current === 1) setInitialLoading(true);
      else setLoading(true);

      const params = {
        tableName: "supplier_invoice_records",
        recordId: id,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };

      const response: IAuditLogResponse = await AuditLogServices.getAuditLogs({ params });
      const newLogs = response.data.results || [];

      setLogs(prev => (pagination.current === 1 ? newLogs : [...prev, ...newLogs]));
      setPagination(prev => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
      setHasMore(newLogs.length === pagination.pageSize);
    } catch (error) {
      console.error("Fetch audit logs error:", error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, pagination?.current, pagination?.pageSize, sortInfo, hasMore]);

  const handleLoadMore = () => {
    setPagination(prev => ({ ...prev, current: prev.current + 1 }));
  };

  const formatValue = (field: string, value: any) => {
    if (value === null || value === undefined || value === "") return "blank";
    switch (field) {
      case "is_active":
        return value ? "Active" : "Inactive";
      case "amount":
      case "total_amount":
        return formatNumberFixed(value) ?? "blank";
      case "due_date":
      case "invoice_date":
      case "transaction_date":
        return formatDateDDMMYYYY(value) ?? "blank";
      default:
        if (typeof value === "boolean") return value ? "Yes" : "No";
        return String(value);
    }
  };

  const auditData: AuditItem[] = logs.map(log => ({
    id: log.id,
    action: log.action,
    changes: log.changes.map(c => ({
      field: fieldLabels[c.field] || c.field,
      from: formatValue(c.field, c.oldValue),
      to: formatValue(c.field, c.newValue),
    })),
    updatedAt: formatDateDDMMYYYY_HHMMSS(log.updatedAt)!,
    user: log.updatedByUser || "System",
  }));

  return (
    <div className="audit-trail-page">
      <BackButton label="Supplier Invoice Record" />
      <h2 className="title-page" style={{ marginBottom: 16 }}>Audit Trail</h2>
      <h4>{decodeURIComponent(supplierCode ?? "")} - {decodeURIComponent(supplierName ?? "")}</h4>
      <div style={{ borderBottom: "1px solid #e8e8e8", marginBottom: 30 }}></div>

      {initialLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <InfiniteAuditTrail
          items={auditData}
          loading={loading}
          hasMore={hasMore}
          initialLoading={initialLoading}
          onLoadMore={handleLoadMore}
        />
      )}
    </div>
  );
};

export default SIRAudiTrailPage;
